import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

import useAppContext from 'flow/hooks/useAppContext';
import MeetingMemberAvatar from 'flow/components/meeting/shared/MeetingMemberAvatar';
import LabelPlate from './main-area/overlays/LabelPlate';
import NamePlate from './main-area/overlays/NamePlate';
import StatusIconPlate from './main-area/overlays/StatusIconPlate';
import SelfViewDockToggle from './main-area/overlays/SelfViewDockToggle';
import {
    selectActiveMeetingHasOtherParticipants,
    selectRightPanelActive,
    selectSenderAudioActive,
    selectSenderName,
} from 'flow/selectors/meeting-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectOwnUserIsSpeaking,
    selectVideoSecondarySenderVideoStream,
    selectVideoSenderVideoActive,
    selectVideoSenderVideoStream,
    selectVideoShowSenderInGridStatus,
} from 'flow/selectors/video-selectors';

import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import { twMerge } from 'tailwind-merge';

interface MeetingLocalVideoProps {
    isSecondary?: boolean;
}

const YOURE_FIRST_DISPLAY_MS = 4000;

const MeetingLocalVideo: React.FC<MeetingLocalVideoProps> = ({
    isSecondary = false,
}) => {
    const { i18next } = useAppContext();
    const videoActive = useAppSelector(selectVideoSenderVideoActive);
    const shouldShowSenderInGrid = useAppSelector(
        selectVideoShowSenderInGridStatus,
    );
    const senderVideoStream = useAppSelector(selectVideoSenderVideoStream);
    const senderSecondaryVideoStream = useAppSelector(
        selectVideoSecondarySenderVideoStream,
    );
    const name = useAppSelector(selectSenderName);
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const rightPanelActive = useAppSelector(selectRightPanelActive);
    // Muted mic shows a status badge next to the name.
    const audioActive = useAppSelector(selectSenderAudioActive);
    const ownUserIsSpeaking = useAppSelector(selectOwnUserIsSpeaking);
    const hasOtherParticipants = useAppSelector(
        selectActiveMeetingHasOtherParticipants,
    );

    // A snackbar-style "You're first" flash on join, not a standing indicator:
    // shows once if nobody else is here yet, then fades on its own timer
    // rather than tracking whether the room later empties back out.
    const [showYoureFirst, setShowYoureFirst] = useState(false);
    const hasShownYoureFirst = useRef(false);
    useEffect(() => {
        // Someone else joining ends the flash early, timer or not — and means
        // we were never first, so mark it shown to stop a later "You're
        // first" if the room empties back out.
        if (hasOtherParticipants) {
            hasShownYoureFirst.current = true;
            setShowYoureFirst(false);
            return undefined;
        }
        if (hasShownYoureFirst.current) {
            return undefined;
        }
        hasShownYoureFirst.current = true;
        setShowYoureFirst(true);
        const timeout = setTimeout(
            () => setShowYoureFirst(false),
            YOURE_FIRST_DISPLAY_MS,
        );
        return () => clearTimeout(timeout);
    }, [hasOtherParticipants]);

    // The corner PiP (self-view docked out of the grid) is narrow: show only the
    // first name there so the mic / camera-muted icons keep their space. The
    // in-grid tile has room for the full name.
    const cleanName = sliceUUIDFromUserName(name);
    const labelName = shouldShowSenderInGrid
        ? cleanName
        : cleanName.split(' ')[0];

    // The secondary tile is the camera PiP shown alongside a screen share; the
    // primary tile is the camera self-view (in the grid or docked to a corner).
    const videoStream = isSecondary
        ? senderSecondaryVideoStream
        : senderVideoStream;
    const hasLocalVideo = videoActive;

    return (
        <div
            className={twMerge(
                'local-video group relative flex h-full w-full overflow-hidden rounded-xl bg-black text-white',
                // Camera off: lift the tile off pure black so it reads as a
                // distinct surface against the black stage — and lighter than
                // the gray-900/80 status plates so they keep their backplate.
                // Also the tile the avatar measures itself against.
                !hasLocalVideo && 'meeting-tile-avatar-container bg-gray-800',
                // Keep the docked self-view PiP clear of the right panel.
                rightPanelActive &&
                    !shouldShowSenderInGrid &&
                    'max-w-[calc(100%-var(--spacing-meeting-side-panel))]',
                !shouldShowSenderInGrid &&
                    // meeting-panel-transition animates inset-inline-end so the
                    // PiP glides aside in step with the opening panel below,
                    // rather than snapping to its panel-clear position.
                    'meeting-panel-transition fixed end-3 bottom-[calc(var(--spacing-meeting-footer)+4px)] z-[11] h-27 w-48',
                !shouldShowSenderInGrid &&
                    rightPanelActive &&
                    'end-[calc(var(--spacing-meeting-side-panel)+16px)]',
            )}
        >
            <SelfViewDockToggle />

            <div
                className={twMerge(
                    // Bound to the tile interior so the name truncates
                    // against the space actually available beside the
                    // mic/camera plates, not the full tile width.
                    'absolute inset-x-3 bottom-3 z-[1] flex items-center gap-2',
                    // The minimized PiP is tight — pull the row in.
                    !shouldShowSenderInGrid && 'bottom-2 gap-1',
                )}
            >
                <NamePlate label={labelName} />
                {!audioActive && <StatusIconPlate iconKey="mic_off" />}
                {!videoActive && <StatusIconPlate iconKey="videocam_off" />}
            </div>

            <MeetingMemberAvatar
                isLocalVideo={true}
                hasVideo={hasLocalVideo}
                name={name}
                videoStream={videoStream}
                extensionKey={ownExtensionKey}
                size="meetingTile"
            />
            {showYoureFirst && !isSecondary && shouldShowSenderInGrid && (
                <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
                    <LabelPlate className="h-auto px-3 py-2.5">
                        {i18next.t('videoconference.video_grid.youre_first')}
                    </LabelPlate>
                </div>
            )}
            {/* Active-speaker frame, drawn above the video/avatar (a ring on the
                container sits beneath them and is hidden by a camera feed).
                Never the own shared-screen stage, which renders elsewhere
                (VID-214 AC10). */}
            {ownUserIsSpeaking && (
                <div className="pointer-events-none absolute inset-0 z-[2] rounded-xl border-4 border-white" />
            )}
        </div>
    );
};

export default MeetingLocalVideo;
